import { Suspense, lazy, useCallback, useEffect, useState } from "react"
import {
  Box,
  Button,
  Link,
  Stack,
  Typography,
} from "@mui/material"
import type { JourneyState } from "@/experience/journey"
import { DESTINATIONS, type Destination } from "@/experience/planets"
import { projectForDestination } from "@/experience/projects"
import { WorkNav } from "@/portfolio/WorkNav"
import { SelectedWork } from "@/portfolio/SelectedWork"
import { ProjectSection } from "@/portfolio/ProjectSection"
import { PORTFOLIO_PROJECTS } from "@/portfolio/projects"

const ExperienceCanvas = lazy(async () => {
  const m = await import("@/experience/ExperienceCanvas")
  return { default: m.ExperienceCanvas }
})

type UiState = JourneyState & { engaged: boolean; near?: Destination | null }

/**
 * Project Hyperspace opening, then Selected Work portfolio with real product shots.
 */
export default function App() {
  const [engageSignal, setEngageSignal] = useState(0)
  const [progress, setProgress] = useState(0)
  const [ui, setUi] = useState<UiState | null>(null)

  useEffect(() => {
    const onScroll = () => {
      const journeyEl = document.getElementById("journey-scroll")
      if (!journeyEl) return
      const max = Math.max(1, journeyEl.offsetHeight - window.innerHeight)
      const y = Math.min(max, Math.max(0, window.scrollY))
      setProgress(y / max)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const onState = useCallback((s: UiState) => {
    setUi(s)
  }, [])

  const goToProjects = useCallback(() => {
    const el = document.getElementById("selected-work")
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
      return
    }
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })
  }, [])

  const mode = ui?.overlayMode ?? "launch"
  const destIndex = Math.min(
    3,
    Math.max(0, Math.floor(((progress - 0.22) / 0.34) * 4)),
  )
  const approxNear =
    mode === "destination" ? DESTINATIONS[destIndex] : (ui?.near ?? null)
  const inJourney = progress < 0.92
  const showNext = inJourney && mode !== "launch" && mode !== "finale"

  return (
    <Box component="main" sx={{ position: "relative", color: "#e8eefc" }}>
      <Suspense fallback={null}>
        <ExperienceCanvas
          engageSignal={engageSignal}
          progress={progress}
          onState={onState}
        />
      </Suspense>

      <Box
        aria-hidden
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          background: `
            linear-gradient(180deg, rgba(2,4,10,0.68) 0%, rgba(2,4,10,0.12) 26%, rgba(2,4,10,0) 46%),
            linear-gradient(0deg, rgba(2,4,10,0.55) 0%, rgba(2,4,10,0.18) 22%, rgba(2,4,10,0) 40%)
          `,
        }}
      />

      <WorkNav />

      {/* Journey spacer: scroll drives 3D flight (kept short so warp does not trap you) */}
      <Box id="journey-scroll" sx={{ position: "relative", minHeight: "240vh" }}>
        <Box
          sx={{
            position: "fixed",
            right: { xs: 8, md: 16 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 3,
            display: inJourney ? "flex" : "none",
            flexDirection: "column",
            gap: 0.75,
            pointerEvents: "none",
            p: 0.75,
            borderRadius: "4px 18px 18px 4px",
            bgcolor: "rgba(4, 6, 12, 0.55)",
            border: "1px solid rgba(255, 90, 70, 0.35)",
            boxShadow: "0 0 24px rgba(255, 60, 40, 0.12)",
            backdropFilter: "blur(6px)",
          }}
          aria-hidden
        >
          <Typography
            sx={{
              fontFamily: '"Space Grotesk", "Sora", system-ui, sans-serif',
              fontSize: { xs: "0.55rem", md: "0.62rem" },
              fontWeight: 700,
              letterSpacing: "0.22em",
              color: "#ff6b55",
              px: 0.75,
              pb: 0.35,
              borderBottom: "2px solid #c44a3a",
              mb: 0.25,
            }}
          >
            NAV
          </Typography>
          {(["LAUNCH", "WARP", "WORLDS", "FRONTIER", "ARRIVAL"] as const).map((label, i) => {
            const active = Math.floor(progress * 4.99) === i
            return (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  minWidth: { xs: 108, md: 148 },
                  borderRadius: "3px 14px 14px 3px",
                  overflow: "hidden",
                  bgcolor: active ? "rgba(255, 72, 48, 0.92)" : "rgba(180, 55, 40, 0.28)",
                  boxShadow: active ? "0 0 16px rgba(255, 70, 40, 0.55)" : "none",
                  outline: active ? "1px solid rgba(255, 200, 170, 0.55)" : "1px solid transparent",
                }}
              >
                <Box
                  sx={{
                    width: { xs: 22, md: 28 },
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: active ? "#ffd4c4" : "rgba(255, 120, 90, 0.45)",
                    color: active ? "#3a0c08" : "rgba(255, 220, 200, 0.85)",
                    fontFamily: '"Space Grotesk", "Sora", system-ui, sans-serif',
                    fontSize: { xs: "0.7rem", md: "0.82rem" },
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                  }}
                >
                  {i + 1}
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Space Grotesk", "Sora", system-ui, sans-serif',
                    fontSize: { xs: "0.72rem", md: "0.92rem" },
                    letterSpacing: { xs: "0.12em", md: "0.16em" },
                    fontWeight: 700,
                    color: active ? "#fff6f0" : "rgba(255, 200, 180, 0.72)",
                    px: { xs: 1, md: 1.25 },
                    py: { xs: 0.65, md: 0.85 },
                    lineHeight: 1.1,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            )
          })}
        </Box>

        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2,
            pointerEvents: "none",
            display: inJourney ? "flex" : "none",
            flexDirection: "column",
            justifyContent: mode === "finale" ? "flex-end" : "flex-start",
            pt: { xs: 7, md: 10 },
            pb: { xs: 6, md: 8 },
            px: { xs: 2.5, md: 4 },
            "& a, & button": { pointerEvents: "auto" },
          }}
        >
          <Stack
            spacing={1.5}
            sx={{
              alignItems: "center",
              textAlign: "center",
              mx: "auto",
              maxWidth: 720,
              opacity: 1,
              transition: "opacity 0.5s ease",
            }}
          >
            {mode === "launch" && (
              <>
                <Typography
                  component="p"
                  sx={{
                    letterSpacing: "0.28em",
                    fontWeight: 700,
                    color: "rgba(160, 220, 255, 0.9)",
                    fontSize: { xs: "0.7rem", md: "0.78rem" },
                  }}
                >
                  AMIR MESHKIN
                </Typography>
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: '"Space Grotesk", "Sora", system-ui, sans-serif',
                    fontWeight: 600,
                    letterSpacing: "-0.03em",
                    lineHeight: 1.05,
                    fontSize: { xs: "2.4rem", sm: "3.4rem", md: "4.4rem" },
                    textShadow: "0 0 40px rgba(80,180,255,0.35)",
                  }}
                >
                  ENGINEERING WHAT COMES NEXT.
                </Typography>
                <Typography
                  sx={{
                    maxWidth: 34 * 16,
                    color: "rgba(210, 225, 255, 0.78)",
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                    lineHeight: 1.55,
                  }}
                >
                  A connected universe of engineering, artificial intelligence, creative
                  technology, and sound.
                </Typography>
              </>
            )}

            {mode === "destination" && approxNear && (
              <Box
                sx={{
                  mt: { xs: "18vh", md: "22vh" },
                  border: "1px solid rgba(140,190,255,0.28)",
                  bgcolor: "rgba(4,8,18,0.62)",
                  backdropFilter: "blur(12px)",
                  maxWidth: projectForDestination(approxNear.id) ? 560 : 420,
                  overflow: "hidden",
                  textAlign: "left",
                  pointerEvents: "auto",
                }}
              >
                {(() => {
                  const project = projectForDestination(approxNear.id)
                  return (
                    <>
                      {project ? (
                        <Box
                          component="a"
                          href={project.href}
                          target={project.href.startsWith("http") ? "_blank" : undefined}
                          rel={project.href.startsWith("http") ? "noreferrer" : undefined}
                          aria-label={`Open ${project.name}`}
                          sx={{
                            display: "block",
                            lineHeight: 0,
                            borderBottom: "1px solid rgba(140,190,255,0.22)",
                          }}
                        >
                          <Box
                            component="img"
                            src={project.image}
                            alt={`${project.name} product desk`}
                            sx={{ width: "100%", height: "auto", display: "block" }}
                          />
                        </Box>
                      ) : null}
                      <Box sx={{ px: 2.5, py: 2 }}>
                        <Typography
                          sx={{
                            letterSpacing: "0.22em",
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            color: "rgba(120,230,255,0.9)",
                            mb: 0.75,
                          }}
                        >
                          {approxNear.label}
                        </Typography>
                        {project ? (
                          <>
                            <Typography
                              sx={{
                                fontFamily: '"Space Grotesk", "Sora", system-ui, sans-serif',
                                fontWeight: 600,
                                letterSpacing: "-0.02em",
                                fontSize: { xs: "1.35rem", md: "1.55rem" },
                                lineHeight: 1.15,
                                mb: 0.75,
                              }}
                            >
                              {project.name}
                            </Typography>
                            <Typography
                              sx={{
                                color: "rgba(220,230,255,0.85)",
                                fontSize: "0.92rem",
                                mb: 1.25,
                              }}
                            >
                              {project.tagline}
                            </Typography>
                            <Link
                              href={project.href}
                              underline="hover"
                              sx={{
                                letterSpacing: "0.14em",
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                color: "rgba(120,230,255,0.95)",
                              }}
                            >
                              OPEN PROJECT
                            </Link>
                          </>
                        ) : (
                          <Typography
                            sx={{ color: "rgba(220,230,255,0.85)", fontSize: "0.95rem" }}
                          >
                            {approxNear.blurb}
                          </Typography>
                        )}
                      </Box>
                    </>
                  )
                })()}
              </Box>
            )}

            {mode === "finale" && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: '"Space Grotesk", "Sora", system-ui, sans-serif',
                    fontWeight: 600,
                    letterSpacing: "-0.03em",
                    fontSize: { xs: "2.2rem", md: "3.4rem" },
                    lineHeight: 1.08,
                    mb: 1,
                  }}
                >
                  MORE THAN CODE.
                </Typography>
                <Typography
                  sx={{
                    letterSpacing: "0.14em",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "rgba(180,160,255,0.85)",
                    mb: 2,
                  }}
                >
                  A WORLD OF POSSIBILITIES.
                </Typography>
                <Button
                  component={Link}
                  href="#selected-work"
                  sx={{
                    letterSpacing: "0.14em",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#041018",
                    bgcolor: "rgba(0,180,255,0.92)",
                    borderRadius: 999,
                    px: 2.5,
                    textDecoration: "none",
                    "&:hover": { bgcolor: "rgba(120,220,255,1)" },
                  }}
                >
                  VIEW SELECTED WORK
                </Button>
              </Box>
            )}

            <Typography
              component="p"
              aria-live="polite"
              sx={{
                pt: mode === "launch" ? 1.5 : 0,
                letterSpacing: "0.22em",
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "rgba(120, 230, 255, 0.85)",
              }}
            >
              {ui?.label ?? "SYSTEM INITIALIZING"}
            </Typography>

            {(ui?.showEngage ?? true) && mode === "launch" && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setEngageSignal((n) => n + 1)
                    const journeyEl = document.getElementById("journey-scroll")
                    const max = Math.max(1, (journeyEl?.offsetHeight ?? 0) - window.innerHeight)
                    // Jump into a brief warp beat, then keep scrolling freely
                    window.scrollTo({ top: max * 0.14, behavior: "smooth" })
                  }}
                  aria-label="Engage warp"
                  sx={{
                    px: 3.5,
                    py: 1.25,
                    borderRadius: 999,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    fontSize: "0.78rem",
                    bgcolor: "rgba(0, 180, 255, 0.92)",
                    color: "#041018",
                    boxShadow: "0 0 28px rgba(80, 200, 255, 0.45)",
                    "&:hover": {
                      bgcolor: "rgba(120, 220, 255, 1)",
                      boxShadow: "0 0 40px rgba(120, 220, 255, 0.55)",
                    },
                  }}
                >
                  ENGAGE WARP
                </Button>
                <Button
                  size="large"
                  onClick={goToProjects}
                  aria-label="Skip to selected work"
                  sx={{
                    px: 3,
                    py: 1.25,
                    borderRadius: 999,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    fontSize: "0.72rem",
                    color: "rgba(200,220,255,0.9)",
                    border: "1px solid rgba(140,170,210,0.45)",
                    bgcolor: "rgba(4,8,18,0.45)",
                    "&:hover": {
                      bgcolor: "rgba(20,40,70,0.7)",
                      borderColor: "rgba(160,210,255,0.7)",
                    },
                  }}
                >
                  SKIP TO WORK
                </Button>
              </Stack>
            )}

            {showNext && (
              <Button
                variant="contained"
                size="large"
                onClick={goToProjects}
                aria-label="Next, view selected work"
                sx={{
                  mt: 2,
                  px: 3.5,
                  py: 1.25,
                  borderRadius: 999,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  fontSize: "0.78rem",
                  bgcolor: "rgba(0, 180, 255, 0.92)",
                  color: "#041018",
                  boxShadow: "0 0 28px rgba(80, 200, 255, 0.45)",
                  pointerEvents: "auto",
                  "&:hover": {
                    bgcolor: "rgba(120, 220, 255, 1)",
                    boxShadow: "0 0 40px rgba(120, 220, 255, 0.55)",
                  },
                }}
              >
                NEXT · SELECTED WORK
              </Button>
            )}

            {mode === "launch" && (
              <Typography
                sx={{
                  letterSpacing: "0.16em",
                  fontSize: "0.62rem",
                  color: "rgba(180, 200, 230, 0.45)",
                  pt: 1,
                }}
              >
                SCROLL TO TRAVEL · NEXT OPENS PROJECTS
              </Typography>
            )}

            {showNext && (
              <Typography
                sx={{
                  letterSpacing: "0.16em",
                  fontSize: "0.62rem",
                  color: "rgba(180, 200, 230, 0.45)",
                }}
              >
                OR KEEP SCROLLING PAST ARRIVAL
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Portfolio content over dimmed space backdrop */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          bgcolor: "rgba(2,4,10,0.92)",
          borderTop: "1px solid rgba(120,160,220,0.18)",
          overflowX: "hidden",
          maxWidth: "100%",
        }}
      >
        <SelectedWork />
        {PORTFOLIO_PROJECTS.map((p) => (
          <ProjectSection key={p.id} project={p} />
        ))}

        <Box
          component="footer"
          sx={{
            px: 3,
            py: 5,
            textAlign: "center",
            borderTop: "1px solid rgba(120,160,220,0.15)",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ justifyContent: "center", flexWrap: "wrap", mb: 1.5 }}
          >
            <Link href="mailto:amir.meshkin@gmail.com" color="inherit" underline="hover">
              Email
            </Link>
            <Link
              href="https://github.com/ameshkin"
              target="_blank"
              rel="noreferrer"
              color="inherit"
              underline="hover"
            >
              GitHub
            </Link>
            <Link
              href="https://getorchestrator.app"
              target="_blank"
              rel="noreferrer"
              color="inherit"
              underline="hover"
            >
              Orchestrator
            </Link>
            <Link
              href="https://blackvelvetrecords.com"
              target="_blank"
              rel="noreferrer"
              color="inherit"
              underline="hover"
            >
              Black Velvet
            </Link>
          </Stack>
          <Typography sx={{ fontSize: "0.65rem", letterSpacing: "0.18em", color: "rgba(160,190,230,0.45)" }}>
            AMIR MESHKIN · SELECTED WORK
          </Typography>
        </Box>
      </Box>

      <Button
        size="small"
        aria-pressed={false}
        aria-label="Sound off"
        disabled
        sx={{
          position: "fixed",
          left: 16,
          bottom: 16,
          zIndex: 4,
          pointerEvents: "auto",
          letterSpacing: "0.16em",
          fontSize: "0.62rem",
          color: "rgba(180,200,230,0.55)",
          border: "1px solid rgba(140,170,210,0.25)",
          minWidth: 0,
          px: 1.25,
        }}
      >
        SOUND OFF
      </Button>
    </Box>
  )
}
